import React, {
  useRef,
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
} from "react";
import { useCommandFlow } from "../hooks/command/useCommandFlow";
import { getTranslation } from "../config/translations";
import useCommandFlowStore from "../store/commandFlowStore";

import { usePersistHistory } from "../hooks/state/usePersistHistory";
import { useResetCommandHistory } from "../hooks/state/useResetCommandHistory";

import { useInitializeCommandFlow } from "../hooks/theme/useInitializeCommandFlow";
import { useCurrentStepChoices } from "../hooks/command/useCurrentStepChoices";
import { useThemeManager } from "../hooks/theme/useThemeManager";
import { useTerminalStyles } from "../hooks/terminal/useTerminalStyles";
import { useInputHandling } from "../hooks/terminal/useInputHandling";
import { useFocusHandling } from "../hooks/terminal/useFocusHandling";
import { useTypingSkip } from "../hooks/terminal/useTypingSkip";
import { handleCommandSubmit } from "../handlers/command/commandInputHandlers";
import TerminalOutput from "./TerminalOutput.jsx";
import PromptDisplay from "./PromptDisplay.jsx";
import LoadingIndicator from "./LoadingIndicator.jsx";

const CommandLine = forwardRef(
  (
    {
      commands,
      theme: themeProp,
      onCommandComplete,
      onCommandStart,
      onCommandAbort,
      onCommandBack,
      onStepStart,
      onStepComplete,
      callbacks = {}, // New prop for custom callbacks
      translations = {},
      promptLabel = ">", // Default value here
      outputDelay = 800, // Default delay for system output in milliseconds
      typingSpeed = 300, // Default typing speed in milliseconds between characters
      promptDelay = 300, // Default delay before prompt appears after output in milliseconds
      sentencePause = 0, // Extra pause in ms after sentence-ending punctuation (0 = disabled)
      height = "500px",
      display = true,
      opacity = 1,
      transition = "opacity 0.3s ease, height 0.6s ease",
    },
    ref
  ) => {
    useInitializeCommandFlow(
      commands,
      translations,
      promptLabel,
      outputDelay,
      typingSpeed,
      promptDelay,
      callbacks,
      sentencePause
    );

    const { currentPrompt, handleUserInput, engine } = useCommandFlow();

    // Add busy state to prevent multiple simultaneous command executions
    const [isBusy, setIsBusy] = useState(false);

    // Wrap the original onCommandComplete to reset busy state
    const wrappedOnCommandComplete = (...args) => {
      setIsBusy(false); // Reset busy state when command completes
      if (onCommandComplete) {
        onCommandComplete(...args);
      }
    };

    // Expose executeCommand method through ref
    useImperativeHandle(
      ref,
      () => ({
        executeCommand: async (commandName, initialData = null) => {
          // Check if already busy (command in progress)
          if (isBusy) {
            console.log("⏸️ Command execution blocked - already busy");
            return;
          }

          if (engine && handleUserInput) {
            try {
              // Set busy state to prevent multiple executions
              setIsBusy(true);

              console.log("🚀 executeCommand called:", {
                commandName,
                initialData,
              });

              // Add to history first
              const { setHistory, history } = useCommandFlowStore.getState();
              if (history.length === 0 || history[0] !== commandName) {
                const newHistory = [commandName, ...history].slice(0, 50);
                setHistory(newHistory);
              }

              // If we have initial data, we need to temporarily store it
              // and let the normal command flow pick it up
              if (initialData && Object.keys(initialData).length > 0) {
                console.log(
                  "📦 Storing initial data for command:",
                  initialData
                );

                // Store the initial data in a temporary location that the engine can access
                window._commandFlowInitialData = {
                  commandName,
                  data: initialData,
                  timestamp: Date.now(),
                };
              }

              // Execute the command using handleUserInput directly
              // Note: Don't await to prevent interfering with session state
              handleUserInput(
                commandName,
                wrappedOnCommandComplete,
                onStepStart,
                onStepComplete
              ).catch((error) => {
                console.error("❌ Error executing command:", error);
                setIsBusy(false); // Reset busy state on error
              });
            } catch (error) {
              console.error("❌ Error executing command:", error);
              setIsBusy(false); // Reset busy state on error
            }
            // Note: Don't put finally block here as it would reset busy state immediately
          } else {
            console.warn("❌ Engine or handleUserInput not available");
            setIsBusy(false); // Reset busy state if engine not available
          }
        },
      }),
      [engine, handleUserInput, isBusy]
    );
    const { suggestionText, setTerminalContainer } = useCommandFlowStore();
    const output = useCommandFlowStore((state) => state.output);
    const inSession = useCommandFlowStore((state) => state.inSession);
    const isLoading = useCommandFlowStore((state) => state.isLoading);

    const isTyping = useCommandFlowStore((state) => state.isTyping);
    const isTypingPrompt = useCommandFlowStore((state) => state.isTypingPrompt);
    const isAwaitingPrompt = useCommandFlowStore(
      (state) => state.isAwaitingPrompt
    );
    const isSkippableDelay = useCommandFlowStore(
      (state) => state.isSkippableDelay
    );

    const theme = useThemeManager(themeProp);
    const terminalRef = useRef(null);
    const inputRef = useRef(null);

    // Dynamic margin for output-swap
    const [showMargin, setShowMargin] = useState(false);

    useEffect(() => {
      // Add margin after typing starts
      if (isTyping && !isLoading) {
        setShowMargin(true);
      }
      // Remove margin when typing is finished
      if (!isTyping && !isLoading) {
        setShowMargin(false);
      }
    }, [isTyping, isLoading]);

    // Pass terminal ref to store for scrolling
    useEffect(() => {
      setTerminalContainer(terminalRef);
    }, [setTerminalContainer]);

    const currentStepChoices = useCurrentStepChoices(engine);

    const { terminalTextStyle, inputStyle, containerStyle, suggestionStyle } =
      useTerminalStyles(height, display, opacity, transition);

    const { handleKeyDown } = useInputHandling(
      currentStepChoices,
      commands,
      currentPrompt
    );

    const { handleInputBlur, handleContainerFocus, handleInputFocus } =
      useFocusHandling(terminalRef);

    usePersistHistory(useCommandFlowStore((state) => state.history));
    useResetCommandHistory();
    useTypingSkip(); // Add spacebar functionality for typing skip

    const onSubmit = (e) => {
      handleCommandSubmit(e, {
        handleUserInput,
        onCommandComplete: wrappedOnCommandComplete,
        onStepStart,
        onCommandAbort,
        onCommandBack,
        onCommandStart,
      });
    };

    const handleContainerClick = () => {
      if (inputRef.current) inputRef.current.focus();
    };

    // Get promptLabel from store for display
    const storedPromptLabel = useCommandFlowStore((state) => state.promptLabel);
    const storedTranslations = useCommandFlowStore(
      (state) => state.translations
    );

    return (
      <div
        style={containerStyle}
        ref={terminalRef}
        tabIndex={-1}
        onClick={handleContainerClick}
        onFocus={handleContainerFocus}
        onBlur={handleInputBlur}
        className="command-flow-terminal"
      >
        <style>{`
        .command-flow-terminal {
          /* Firefox scrollbar */
          scrollbar-width: auto;
          scrollbar-color: ${
            theme.scrollbarThumbColor || "rgba(255, 255, 255, 0.3)"
          } ${theme.scrollbarTrackColor || "rgba(0, 0, 0, 0.2)"};
        }
        /* WebKit scrollbar (Chrome, Safari, Edge) */
        .command-flow-terminal::-webkit-scrollbar {
          width: 12px;
        }
        .command-flow-terminal::-webkit-scrollbar-track {
          background: ${theme.scrollbarTrackColor || "rgba(0, 0, 0, 0.2)"};
          border-radius: ${theme.borderRadius || "8px"};
        }
        .command-flow-terminal::-webkit-scrollbar-thumb {
          background: ${
            theme.scrollbarThumbColor || "rgba(255, 255, 255, 0.3)"
          };
          border-radius: ${theme.borderRadius || "8px"};
        }
        .command-flow-terminal::-webkit-scrollbar-thumb:hover {
          background: ${
            theme.scrollbarThumbHoverColor || "rgba(255, 255, 255, 0.5)"
          };
        }
        .output-swap {
          min-height: 1.2em;
          opacity: 1;
          transition: min-height 0.3s, opacity 0.2s, margin-bottom 0.3s;
          margin-bottom: 0;
        }
        .output-swap.with-margin {
          margin-bottom: 0em;
        }
        .output-swap.loading {
          opacity: 1;
        }
        @keyframes rcf-fadein {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .rcf-loading-delayed {
          animation: rcf-fadein 0s 500ms both;
        }
      `}</style>
        <TerminalOutput theme={theme} terminalTextStyle={terminalTextStyle} />

        <div
          className={`output-swap${showMargin ? " with-margin" : ""}${
            isLoading && !isTyping && !isTypingPrompt
              ? " loading"
              : ""
          }`}
        >
          {isLoading && !isTyping && !isTypingPrompt ? (
            <div className="rcf-loading-delayed">
              <LoadingIndicator
                translations={storedTranslations}
                animated={true}
              />
            </div>
          ) : !isTyping || isTypingPrompt || isAwaitingPrompt || isSkippableDelay ? (
            // During active processing (typing, delays, awaiting) with no custom prompt,
            // hide the default ">>" to prevent it flashing between command steps.
            // Also hide during frame mode when no prompt is set — the frame controls
            // its own prompt lifecycle and the default ">" must not leak through.
            !currentPrompt && !isTypingPrompt && (isTyping || isSkippableDelay || isAwaitingPrompt || useCommandFlowStore.getState().frameMode) ? null : <PromptDisplay
              currentPrompt={currentPrompt}
              promptLabel={storedPromptLabel}
              inputRef={inputRef}
              onSubmit={onSubmit}
              onKeyDown={handleKeyDown}
              onFocus={handleInputFocus}
              onBlur={handleInputBlur}
              isLoading={isLoading || isTypingPrompt}
              isTypingPrompt={isTypingPrompt}
              theme={theme}
              inputStyle={inputStyle}
              terminalTextStyle={terminalTextStyle}
            />
          ) : null}
        </div>

        {suggestionText && <div style={suggestionStyle}>{suggestionText}</div>}
      </div>
    );
  }
);

export default CommandLine;
